import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getCostCenterTypes, getItCodesLookup, getTypeOfTaxes, getNatureOfTaxes, getAllDcaForSubDca,
    getApprovalStatusDCA, getApprovedDcaById, getAllDCAForDCASubDCA, saveNewDCA, updateDCA, saveNewSubDCA,
} from '../../api/AccountsAPI/dcaCreationAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

// ── Thunks ─────────────────────────────────────────────────────────────────────

export const fetchCCTypes = createAsyncThunk('dcaCreation/fetchCCTypes', async (_, { rejectWithValue }) => {
    try { const res = await getCostCenterTypes(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centre types')); }
});

export const fetchItCodesLookup = createAsyncThunk('dcaCreation/fetchItCodes', async (_, { rejectWithValue }) => {
    try { const res = await getItCodesLookup(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch IT codes')); }
});

export const fetchTypeOfTaxes = createAsyncThunk('dcaCreation/fetchTypeOfTaxes', async (_, { rejectWithValue }) => {
    try { const res = await getTypeOfTaxes(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch type of taxes')); }
});

export const fetchNatureOfTaxes = createAsyncThunk('dcaCreation/fetchNatureOfTaxes', async (_, { rejectWithValue }) => {
    try { const res = await getNatureOfTaxes(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch nature of taxes')); }
});

export const fetchDcasForSubDca = createAsyncThunk('dcaCreation/fetchDcasForSubDca', async (_, { rejectWithValue }) => {
    try { const res = await getAllDcaForSubDca(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch account heads')); }
});

export const fetchApprovalStatusDCA = createAsyncThunk('dcaCreation/fetchApprovalStatus', async (roleId, { rejectWithValue }) => {
    try { const res = await getApprovalStatusDCA('Account Head', roleId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch allowed actions')); }
});

export const fetchApprovedDcaById = createAsyncThunk('dcaCreation/fetchApprovedDcaById', async ({ DcaId, Dcatypeid }, { rejectWithValue }) => {
    try { const res = await getApprovedDcaById(DcaId, Dcatypeid); return res?.Data || null; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch account head details')); }
});

export const fetchDCAGrid = createAsyncThunk('dcaCreation/fetchGrid', async (_, { rejectWithValue }) => {
    try { const res = await getAllDCAForDCASubDCA(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch account heads')); }
});

export const submitNewDCA = createAsyncThunk('dcaCreation/submitNew', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveNewDCA(payload);
        const status = res?.Data;
        if (status !== 'Submitted' && status !== 'Submited') return rejectWithValue(status || 'Failed to save account head');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save account head')); }
});

export const submitUpdateDCA = createAsyncThunk('dcaCreation/submitUpdate', async (payload, { rejectWithValue }) => {
    try {
        const res = await updateDCA(payload);
        const status = res?.Data;
        if (status !== 'Updated') return rejectWithValue(status || 'Failed to update account head');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to update account head')); }
});

export const submitNewSubDCA = createAsyncThunk('dcaCreation/submitNewSubDCA', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveNewSubDCA(payload);
        const status = res?.Data;
        if (status !== 'Submitted' && status !== 'Submited') return rejectWithValue(status || 'Failed to save sub account head');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save sub account head')); }
});

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    ccTypes: [], ccTypesLoading: false,
    itCodes: [], itCodesLoading: false,
    typeOfTaxes: [], typeOfTaxesLoading: false,
    natureOfTaxes: [], natureOfTaxesLoading: false,
    dcasForSubDca: [], dcasForSubDcaLoading: false,
    allowedActions: [], allowedActionsLoading: false,
    approvedDca: null, approvedDcaLoading: false,

    grid: [], gridLoading: false, gridError: null,

    saveLoading: false, saveResult: null, saveError: null,
    updateLoading: false, updateResult: null, updateError: null,
    saveSubDCALoading: false, saveSubDCAResult: null, saveSubDCAError: null,
};

const dcaCreationSlice = createSlice({
    name: 'dcaCreation',
    initialState,
    reducers: {
        clearSaveResult: (s) => { s.saveResult = null; s.saveError = null; },
        clearUpdateResult: (s) => { s.updateResult = null; s.updateError = null; },
        clearSaveSubDCAResult: (s) => { s.saveSubDCAResult = null; s.saveSubDCAError = null; },
        clearApprovedDca: (s) => { s.approvedDca = null; },
        resetDcaCreation: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCCTypes.pending, (s) => { s.ccTypesLoading = true; })
            .addCase(fetchCCTypes.fulfilled, (s, a) => { s.ccTypesLoading = false; s.ccTypes = a.payload; })
            .addCase(fetchCCTypes.rejected, (s) => { s.ccTypesLoading = false; });

        builder
            .addCase(fetchItCodesLookup.pending, (s) => { s.itCodesLoading = true; })
            .addCase(fetchItCodesLookup.fulfilled, (s, a) => { s.itCodesLoading = false; s.itCodes = a.payload; })
            .addCase(fetchItCodesLookup.rejected, (s) => { s.itCodesLoading = false; });

        builder
            .addCase(fetchTypeOfTaxes.pending, (s) => { s.typeOfTaxesLoading = true; })
            .addCase(fetchTypeOfTaxes.fulfilled, (s, a) => { s.typeOfTaxesLoading = false; s.typeOfTaxes = a.payload; })
            .addCase(fetchTypeOfTaxes.rejected, (s) => { s.typeOfTaxesLoading = false; });

        builder
            .addCase(fetchNatureOfTaxes.pending, (s) => { s.natureOfTaxesLoading = true; })
            .addCase(fetchNatureOfTaxes.fulfilled, (s, a) => { s.natureOfTaxesLoading = false; s.natureOfTaxes = a.payload; })
            .addCase(fetchNatureOfTaxes.rejected, (s) => { s.natureOfTaxesLoading = false; });

        builder
            .addCase(fetchDcasForSubDca.pending, (s) => { s.dcasForSubDcaLoading = true; })
            .addCase(fetchDcasForSubDca.fulfilled, (s, a) => { s.dcasForSubDcaLoading = false; s.dcasForSubDca = a.payload; })
            .addCase(fetchDcasForSubDca.rejected, (s) => { s.dcasForSubDcaLoading = false; });

        builder
            .addCase(fetchApprovalStatusDCA.pending, (s) => { s.allowedActionsLoading = true; })
            .addCase(fetchApprovalStatusDCA.fulfilled, (s, a) => { s.allowedActionsLoading = false; s.allowedActions = a.payload; })
            .addCase(fetchApprovalStatusDCA.rejected, (s) => { s.allowedActionsLoading = false; });

        builder
            .addCase(fetchApprovedDcaById.pending, (s) => { s.approvedDcaLoading = true; })
            .addCase(fetchApprovedDcaById.fulfilled, (s, a) => { s.approvedDcaLoading = false; s.approvedDca = a.payload; })
            .addCase(fetchApprovedDcaById.rejected, (s) => { s.approvedDcaLoading = false; s.approvedDca = null; });

        builder
            .addCase(fetchDCAGrid.pending, (s) => { s.gridLoading = true; s.gridError = null; })
            .addCase(fetchDCAGrid.fulfilled, (s, a) => { s.gridLoading = false; s.grid = a.payload; })
            .addCase(fetchDCAGrid.rejected, (s, a) => { s.gridLoading = false; s.gridError = a.payload; });

        builder
            .addCase(submitNewDCA.pending, (s) => { s.saveLoading = true; s.saveError = null; s.saveResult = null; })
            .addCase(submitNewDCA.fulfilled, (s, a) => { s.saveLoading = false; s.saveResult = a.payload; })
            .addCase(submitNewDCA.rejected, (s, a) => { s.saveLoading = false; s.saveError = a.payload; });

        builder
            .addCase(submitUpdateDCA.pending, (s) => { s.updateLoading = true; s.updateError = null; s.updateResult = null; })
            .addCase(submitUpdateDCA.fulfilled, (s, a) => { s.updateLoading = false; s.updateResult = a.payload; })
            .addCase(submitUpdateDCA.rejected, (s, a) => { s.updateLoading = false; s.updateError = a.payload; });

        builder
            .addCase(submitNewSubDCA.pending, (s) => { s.saveSubDCALoading = true; s.saveSubDCAError = null; s.saveSubDCAResult = null; })
            .addCase(submitNewSubDCA.fulfilled, (s, a) => { s.saveSubDCALoading = false; s.saveSubDCAResult = a.payload; })
            .addCase(submitNewSubDCA.rejected, (s, a) => { s.saveSubDCALoading = false; s.saveSubDCAError = a.payload; });
    },
});

export const {
    clearSaveResult, clearUpdateResult, clearSaveSubDCAResult, clearApprovedDca, resetDcaCreation,
} = dcaCreationSlice.actions;

export const selectDcaCreation = (s) => s.dcaCreation;

export default dcaCreationSlice.reducer;
